/**
 * SUMMARY MODAL COMPONENT
 * Generic modal for displaying data summaries
 * @version 1.0.0
 * @production-ready
 */

import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, BarChart3, Database, Download } from 'lucide-react';

export interface SummaryColumn {
  key: string;
  label: string;
  width?: string;
  format?: (value: any) => string;
  align?: 'left' | 'center' | 'right';
}

export interface SummaryModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  data: any[];
  columns: SummaryColumn[];
  loading?: boolean;
  emptyMessage?: string;
  onExport?: () => void;
  customActions?: React.ReactNode;
  maxHeight?: string;
}

export const SummaryModal: React.FC<SummaryModalProps> = ({
  isOpen,
  onClose,
  title,
  subtitle,
  data,
  columns,
  loading = false,
  emptyMessage = 'No data available',
  onExport,
  customActions,
  maxHeight = '70vh'
}) => {
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div
        className="fixed inset-0 flex items-center justify-center p-4"
        style={{
          background: 'rgba(0, 0, 0, 0.6)',
          zIndex: 9998,
          backdropFilter: 'blur(2px)'
        }}
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: -20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: -20 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          className="rounded-lg max-w-6xl w-[95vw] sm:w-[90vw] md:w-[85vw] lg:w-[80vw] overflow-hidden flex flex-col"
          style={{
            background: 'var(--color-white)',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            zIndex: 9999,
            maxHeight: '90vh',
            margin: 'auto'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div
            className="flex justify-between items-center p-4 border-b text-white"
            style={{
              background: 'linear-gradient(to right, var(--color-primary-blue), var(--color-secondary-tata-blue))',
              borderBottom: '1px solid var(--color-gray-200)'
            }}
          >
            <div className="flex items-center gap-2">
              <BarChart3 size={20} />
              <div>
                <h2 className="text-lg font-bold">{title}</h2>
                {subtitle && <p className="text-sm opacity-90">{subtitle}</p>}
              </div>
            </div>

            <div className="flex items-center gap-2">
              {onExport && (
                <button
                  onClick={onExport}
                  className="p-2 hover:bg-white/20 rounded transition-colors"
                  title="Export data"
                >
                  <Download size={18} />
                </button>
              )}
              
              {customActions}
              
              <button
                onClick={onClose}
                className="p-2 hover:bg-white/20 rounded transition-colors"
                title="Close"
              >
                <X size={20} />
              </button>
            </div>
          </div>

          {/* Content */}
          <div
            className="p-4 overflow-auto flex-1"
            style={{ 
              maxHeight: `calc(${maxHeight} - 140px)`,
              minHeight: '200px'
            }}
          >
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div 
                  className="animate-spin h-12 w-12 border-4 rounded-full"
                  style={{
                    borderColor: 'var(--color-primary-blue)',
                    borderTopColor: 'transparent'
                  }}
                ></div>
              </div>
            ) : data.length > 0 ? (
              <>
                <div className="mb-4 text-sm" style={{ color: '#4B5563' }}>
                  Found {data.length} record{data.length !== 1 ? 's' : ''}
                </div>
                
                <div className="overflow-x-auto">
                  <table
                    className="min-w-full text-xs"
                    style={{ border: '1px solid var(--color-gray-300)' }}
                  >
                    <thead style={{ background: 'var(--color-gray-100)' }} className="sticky top-0">
                      <tr>
                        {columns.map((col) => (
                          <th
                            key={col.key}
                            className="border p-2 font-semibold"
                            style={{
                              borderColor: 'var(--color-gray-300)',
                              textAlign: col.align || 'left',
                              width: col.width
                            }}
                          >
                            {col.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {data.map((row, idx) => (
                        <tr
                          key={idx}
                          style={{
                            background: idx % 2 === 0 ? 'var(--color-white)' : 'var(--color-gray-50)'
                          }}
                          className="hover:bg-[#F2F8FC]"
                        >
                          {columns.map((col) => (
                            <td
                              key={col.key}
                              className="border p-2"
                              style={{
                                borderColor: 'var(--color-gray-300)',
                                textAlign: col.align || 'left'
                              }}
                            >
                              {col.format
                                ? col.format(row[col.key])
                                : row[col.key] != null
                                  ? String(row[col.key])
                                  : '-'}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <div className="text-center py-12">
                <Database size={48} className="mx-auto mb-4" style={{ color: '#D1D5DB' }} />
                <p className="text-lg" style={{ color: '#6B7280' }}>
                  {emptyMessage}
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div
            className="border-t p-4 flex justify-end gap-2 flex-shrink-0"
            style={{
              background: 'var(--color-gray-50)',
              borderTop: '1px solid var(--color-gray-200)'
            }}
          >
            <button
              onClick={onClose}
              className="px-6 py-2 rounded font-medium transition-colors hover:opacity-90"
              style={{
                background: 'var(--color-primary-blue)',
                color: 'var(--color-white)',
                minWidth: '80px'
              }}
            >
              Close
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};
